if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // TMS fallback from dataset for initial render
      this.currentTms = parseInt(this.dataset.typicalMonthlySales || '0', 10);

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        this.currentTms
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('.js-inventory-quantity');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];

        // Resolve TMS for this variant (may be 0/undefined)
        this.currentTms = parseInt(inventory.tms || 0, 10);

        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          this.currentTms
        );

        const inventoryDataElement = document.getElementById('variant-inventory-data');
        const inventoryData = JSON.parse(inventoryDataElement.textContent);
        const variantInventory = inventoryData.find(
          (data) => data.id === evt.detail.variant.id
        );

        if (variantInventory) {
          const mDisplay = variantInventory.melbourne >= 10 ? "10+" : variantInventory.melbourne;
          const bDisplay = variantInventory.balcatta >= 10 ? "10+" : variantInventory.balcatta;
          const pDisplay = variantInventory.perth >= 10 ? "10+" : variantInventory.perth;

          const inventoryLevelElement = this.querySelector('.js-inventory-level');
          inventoryLevelElement.innerHTML = `Melbourne: ${mDisplay}, Balcatta: ${bDisplay}, Canning Vale: ${pDisplay}`;
        } else {
          console.warn('No inventory data found for the selected variant.');
        }

      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {number} tmsArg - typical monthly sales (may be 0)
     */
    updateInventory(count, available, inventoryPolicy, tmsArg) {
      // Derive dynamic thresholds from TMS if available, otherwise use theme thresholds
      const tms = parseInt(tmsArg || this.currentTms || this.dataset.typicalMonthlySales || '0', 10);

      let thresholdVeryLow;
      let thresholdLow;

      if (tms > 0) {
        // Very low = ceil(50% of TMS), Low = up to TMS-1
        const halfUp = Math.ceil(tms / 2);

        if (tms <= 2) {
          // Preserve a warning band for very small TMS values
          thresholdVeryLow = 1;
          thresholdLow = tms; // count == TMS still considered "low"
        } else {
          thresholdVeryLow = Math.max(1, halfUp);
          thresholdLow = Math.max(thresholdVeryLow, tms - 1);
        }
      } else {
        // No TMS - fall back to theme thresholds
        thresholdVeryLow = parseInt(this.dataset.thresholdVeryLow, 10);
        thresholdLow = parseInt(this.dataset.thresholdLow, 10);
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= thresholdVeryLow) {
        inventoryLevel = 'very_low';
      } else if (count <= thresholdLow) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not - unchanged from original
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed - unchanged from original
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator - use TMS as effective scale if available
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          const baseScale = parseInt(this.dataset.scale, 10);
          const effectiveScale = tms > 0 ? tms : baseScale;

          let newWidth;
          if ((count >= effectiveScale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / effectiveScale) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
